/**
 * Anti-bot bootstrap logic for determining whether the reCAPTCHA Enterprise
 * script should be injected into the page.
 *
 * This module mirrors the IIFE logic in index.html so that both the static
 * bootstrap and the application code share a single, testable decision function.
 */

const INERT_PLACEHOLDERS = ['local-mock', 'mock', 'test', ''];

/**
 * Determines whether the reCAPTCHA Enterprise script should be injected.
 *
 * Returns false (skip injection) when:
 * - The mock antibot flag is active (FF_MOCK_ANTIBOT === 'true')
 * - The site key is empty, a known placeholder, or an unsubstituted template
 */
/**
 * Detects a site key that must not trigger reCAPTCHA injection: empty, a known
 * placeholder, or an unsubstituted `%TEMPLATE%` value.
 *
 * @param siteKey Configured reCAPTCHA site key.
 * @returns `true` when the key is inert and injection must be skipped.
 */
export function isInertSiteKey(siteKey: string): boolean {
  if (!siteKey) return true;
  if (siteKey.includes('%')) return true;
  return INERT_PLACEHOLDERS.includes(siteKey.toLowerCase());
}

/**
 * Determines whether the reCAPTCHA Enterprise script should be injected. Skips
 * injection when the mock anti-bot flag is on or the site key is inert.
 *
 * @param env Environment map providing `FF_MOCK_ANTIBOT` and `ANTI_BOT_PROVIDER_ID`.
 * @returns `true` when the real reCAPTCHA script should load.
 */
export function shouldInjectRecaptcha(
  env: Record<string, string | undefined>,
): boolean {
  if (env.FF_MOCK_ANTIBOT === 'true') return false;
  return !isInertSiteKey(env.ANTI_BOT_PROVIDER_ID || '');
}

/**
 * Returns the reCAPTCHA site key when injection should proceed, else `null`.
 *
 * @param env Environment map providing anti-bot configuration.
 * @returns The site key to inject, or `null` when injection is disabled.
 */
export function getRecaptchaSiteKey(
  env: Record<string, string | undefined>,
): string | null {
  if (!shouldInjectRecaptcha(env)) return null;
  return env.ANTI_BOT_PROVIDER_ID || null;
}
